"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var lodash_1 = require("lodash");
var creditCardType = require("credit-card-type");
var moment = require("moment");
var date_1 = require("./date");
var objectAccess_1 = require("./objectAccess");
var sortCode_1 = require("./sortCode");
var cardNumberParse_1 = require("./cardNumberParse");
// Multiple of 4 for feb 29th
var MAXIMUM_AGE = 120;
var rules = {
    isAtLeast: function (value, constraint) {
        var strValue = value.toString().replace(/,/g, '');
        var income = parseInt(strValue, 10);
        return income >= constraint;
    },
    isDateOfBirth: function (value) {
        if (value === undefined || value === null) {
            return true;
        }
        try {
            for (var i in value) {
                if (value.hasOwnProperty(i) && ([undefined, null, ''].indexOf(value[i]) >= 0)) {
                    return false;
                }
            }
            var today = moment();
            var birthday = moment(value.year + "-" + value.month + "-" + value.day);
            return birthday.add(MAXIMUM_AGE, 'year').isAfter(today);
        }
        catch (e) {
            if (e instanceof date_1.DateError) {
                return false;
            }
            throw e;
        }
    },
    isDateInTheFuture: function (value, constraint) {
        var insertedDate = prepareValidDate(value);
        return (typeof insertedDate === 'boolean') ? insertedDate :
            moment(insertedDate).isSameOrAfter(moment(), constraint);
    },
    isDateInThePast: function (value, constraint) {
        var insertedDate = prepareValidDate(value);
        return (typeof insertedDate === 'boolean') ? insertedDate :
            moment(insertedDate).isSameOrBefore(moment(), constraint);
    },
    ageIsOver: function (value, constraint) {
        if (value === undefined || value === null) {
            return true;
        }
        var today = moment();
        var birthday = moment(value.year + "-" + value.month + "-" + value.day);
        return birthday.add(18, 'year').isBefore(today);
    },
    isRequired: function (value) {
        if (value === undefined || value === null) {
            return false;
        }
        if (typeof value === 'number' && (isNaN(value))) {
            return false;
        }
        if (typeof value === 'string' && value.length === 0) {
            return false;
        }
        return true;
    },
    isRequiredSortCode: function (value) {
        if (value === undefined || value === null) {
            return false;
        }
        if (value.first === '' && value.second === '' && value.third === '') {
            return false;
        }
        return true;
    },
    isRequiredIf: function (value, constraint, currentState) {
        if (typeof constraint === 'function') {
            // constraint should return true if required
            if (constraint(currentState)) {
                return rules.isRequired(value);
            }
            return true;
        }
        var stateValue = objectAccess_1.resolveKey(constraint.field, currentState);
        if (constraint.comparator === undefined && (stateValue === undefined || stateValue === constraint.value)) {
            return rules.isRequired(value);
        }
        return true;
    },
    hasLength: function (value, constraint) {
        if (value === undefined || value === null || value === '') {
            return true;
        }
        if (typeof value === 'number' && !isNaN(value)) {
            value = "" + value;
        }
        if (typeof value !== 'string') {
            return false;
        }
        if (constraint.min && value.length < constraint.min) {
            return false;
        }
        if (constraint.max && value.length > constraint.max) {
            return false;
        }
        return true;
    },
    isFullDate: function (value, constraint) {
        if (constraint === void 0) { constraint = true; }
        if (value === undefined || value === null) {
            return true;
        }
        return (value.day.match(/^[0-9]{2}$/) !== null &&
            value.month.match(/^[0-9]{2}$/) !== null &&
            (constraint ? value.year.match(/^[0-9]{4}$/) !== null : value.year.match(/^[0-9]{2}$/) !== null));
    },
    isEmail: function (value) {
        var emailRegex = /^[a-z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i;
        return emailRegex.test(value);
    },
    isPostcode: function (value) {
        var postcodeRegEx = /^[0-9A-Z]{5,7}$/i;
        if (value !== '' && value !== undefined) {
            return postcodeRegEx.test(value.replace(/\s/g, ''));
        }
        else {
            return value === undefined;
        }
    },
    isPositiveAmount: function (value) {
        return parseInt(value, 10) >= 0;
    },
    validRegex: function (value, constraint) {
        return value === undefined || value === null || value === '' || constraint.test(value.trim());
    },
    passesConstraint: function (value, constraint, currentState) {
        // I don't want to compare the value, I just want to execute a constraint
        if (typeof constraint === 'function') {
            return !constraint(currentState);
        }
        return constraint;
    },
    compareTo: function (value, constraint, currentState) {
        if (typeof constraint === 'function') {
            return !constraint(currentState);
        }
        return constraint;
    },
    isValidSortCode: function (value, constraint) {
        var sortCode = sortCode_1.sortCodeObjectToString(value);
        return rules.hasLength(sortCode, { min: 6, max: 6 });
    },
    shouldBe: function (value, constraint) {
        return value === constraint;
    },
    shouldNotBe: function (value, constraint) {
        return value !== constraint;
    },
    shouldMatchField: function (fieldValue, constraint, currentState) {
        var comparison = lodash_1.get(currentState, constraint.key);
        return fieldValue === comparison;
    },
    notTheSameAs: function (object, constraint, currentState) {
        var comparison = objectAccess_1.resolveKey(constraint.key, currentState);
        var fields = constraint.fields;
        // There is any different field or is no non-empty field, ie all are empty
        return (fields.filter(function (key) { return object[key] !== comparison[key]; }).length ||
            !fields.filter(function (key) { return object[key] && comparison[key]; }).length);
    },
    isCreditCardNumberValid: function (value) {
        if (!/^([0-9]{3,4}\s?){3,5}([0-9]{1,3})$/g.test(value)) {
            return false;
        }
        var parsedValue = cardNumberParse_1.cardNumberParse(value);
        if (parsedValue.length < 12 || parsedValue.length > 19) {
            return false;
        }
        var cardTypes = creditCardType(parsedValue);
        return cardTypes.length === 1;
    },
    isCreditAnAcceptedType: function (value, constraint) {
        var parsedValue = cardNumberParse_1.cardNumberParse(value);
        var cardTypes = creditCardType(parsedValue);
        // we check the credit card number is valid and is in the array of the accepted ones
        return cardTypes.length === 1 && constraint.indexOf(cardTypes[0].type) !== -1;
    },
    isValidType: function (file, constraint, currentState) {
        if (!file || !file.type) {
            return true;
        }
        if (!constraint.documentType ||
            constraint.documentType === 'rear' && currentState.selected_id !== 'id-card') {
            return true;
        }
        return constraint.validTypes.indexOf(file.type) !== -1;
    },
    isValidSize: function (file, constraint) {
        if (!file || !file.size) {
            return true;
        }
        return constraint.maximumFileSize > file.size;
    }
};
var prepareValidDate = function (value) {
    if (value === undefined || value === null) {
        return true;
    }
    try {
        for (var i in value) {
            if (value.hasOwnProperty(i) && ([undefined, null, ''].indexOf(value[i]) >= 0)) {
                return false;
            }
        }
        var year = (value.year.length < 4) ? moment(value.year, 'YY').format('YYYY') : value.year;
        return moment([year, "" + (+value.month - 1), value.day]);
    }
    catch (e) {
        if (e instanceof date_1.DateError) {
            return false;
        }
        throw e;
    }
};
exports.default = rules;
